import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Phone } from "lucide-react";
import { PRODUCTS, PRODUCT_DETAILS } from "@/lib/api";
import { ApplicationForm } from "@/components/ApplicationForm";

export default function ProductDetail() {
  const { key } = useParams();
  const nav = useNavigate();
  const product = PRODUCTS.find((p) => p.key === key);
  const detail = PRODUCT_DETAILS[key];
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); setActive(0); }, [key]);

  if (!product || !detail) {
    return (
      <div className="min-h-screen bg-oed-base flex flex-col items-center justify-center gap-6">
        <p className="text-white/60">Product not found.</p>
        <Link to="/" className="bg-white text-black px-6 py-3 text-sm">Back home</Link>
      </div>
    );
  }

  const others = PRODUCTS.filter((p) => p.key !== key);

  return (
    <div className="min-h-screen bg-oed-base">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-2xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="pd-home-link" className="flex items-center gap-3 group">
            <img src="/logo.png" alt="OED Glampire Network" className="h-11 w-11 object-contain" />
            <span className="hidden sm:block text-[11px] uppercase tracking-[0.3em] text-white/60 group-hover:text-oed-gold transition-colors">OED Glampire Network</span>
          </Link>
          <button data-testid="pd-inquire-nav" onClick={() => setOpen(true)} className="bg-white text-black px-6 py-2.5 text-sm font-medium hover:bg-oed-gold transition-colors">
            Inquire
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <button data-testid="pd-back" onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-10">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="overflow-hidden border border-white/10 aspect-[4/3]">
              <img src={detail.gallery[active]} alt={product.title} className="w-full h-full object-cover" data-testid="pd-main-image" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {detail.gallery.map((g, i) => (
                <button
                  key={i}
                  data-testid={`pd-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`overflow-hidden aspect-[4/3] border transition-colors ${active === i ? "border-oed-gold" : "border-white/10 hover:border-white/30"}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}>
            <p className="text-xs uppercase tracking-[0.3em] text-oed-gold">{product.tag}</p>
            <h1 className="mt-4 font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-5xl">{product.title}</h1>
            <p className="mt-4 text-lg text-white/70">{detail.subtitle}</p>
            <p className="mt-6 text-base text-white/60 leading-relaxed max-w-lg">{detail.blurb}</p>

            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {detail.specs.map((s) => (
                <div key={s.label} className="bg-oed-surface p-5" data-testid={`pd-spec-${s.label.toLowerCase()}`}>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{s.label}</p>
                  <p className="mt-1.5 font-display font-bold text-lg tracking-tight">{s.value}</p>
                </div>
              ))}
            </div>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {detail.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-white/70">
                  <Check size={16} className="mt-0.5 shrink-0 text-oed-gold" /> {f}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button data-testid="pd-inquire-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-3 bg-oed-gold text-black px-8 py-4 text-sm font-medium hover:bg-oed-goldHover transition-colors">
                Inquire About {product.title} <ArrowUpRight size={18} />
              </button>
              <a href="tel:2562687146" data-testid="pd-call" className="inline-flex items-center gap-2 border border-white/20 px-8 py-4 text-sm text-white/80 hover:bg-white/10 transition-colors">
                <Phone size={16} /> 256-268-7146
              </a>
            </div>
          </motion.div>
        </div>

        <div className="mt-24 border-t border-white/10 pt-12">
          <p className="text-xs uppercase tracking-[0.3em] text-oed-gold mb-8">Explore More Products</p>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {others.map((p) => (
              <Link key={p.key} to={`/products/${p.key}`} data-testid={`pd-related-${p.key}`} className="group bg-oed-surface p-6">
                <div className="overflow-hidden aspect-[4/3]">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <h3 className="mt-4 font-display font-bold text-xl tracking-tight group-hover:text-oed-gold transition-colors">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <ApplicationForm open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
